import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { useAuth } from "@/auth/auth-context";
import { ROUTE_PATHS, getDashboardDomainUrl } from "@/routing/domainRoutes";

export default function Navbar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileMenuRendered, setMobileMenuRendered] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const location = useLocation();
  const navLinks = [
    { label: t("nav.features"), href: "/#features" },
    { label: t("nav.howItWorks"), href: "/#how-it-works" },
    { label: t("nav.pricing"), href: "/pricing" },
    { label: t("nav.faq"), href: "/#faq" },
  ];

  const scrollToSection = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith("/#")) {
      const id = href.slice(2);
      if (location.pathname === "/") {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      } else {
        window.location.href = href;
      }
    }
  };

  useEffect(() => {
    if (mobileOpen) {
      setMobileMenuVisible(false);
      setMobileMenuRendered(true);
      const timeout = window.setTimeout(() => setMobileMenuVisible(true), 10);
      return () => window.clearTimeout(timeout);
    }

    setMobileMenuVisible(false);
    if (!mobileMenuRendered) return;
    const timeout = window.setTimeout(() => setMobileMenuRendered(false), 240);
    return () => window.clearTimeout(timeout);
  }, [mobileOpen, mobileMenuRendered]);

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg">
      <div className="container mx-auto grid h-16 grid-cols-[auto_1fr_auto] items-center px-4 lg:px-8">
        <Link to="/" className="flex items-center gap-2">
          <img src="/workscope-mark.svg" alt="WorkScope" className="h-8 w-8 object-contain" width={32} height={32} />
          <span className="text-xl font-bold text-foreground">WorkScope</span>
        </Link>

        <div className="hidden items-center justify-center gap-8 px-6 md:flex">
          {navLinks.map((link) =>
            link.href.startsWith("/#") ? (
              <button
                key={link.label}
                onClick={() => scrollToSection(link.href)}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </button>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </Link>
            ),
          )}
        </div>

        <div className="hidden min-w-[320px] items-center justify-end gap-3 md:flex lg:min-w-[360px]">
          <LanguageSwitcher compact className="shrink-0" />
          {user ? (
            <Button size="sm" className="min-w-[160px] bg-accent text-accent-foreground hover:bg-accent/90" asChild>
              <a href={getDashboardDomainUrl(ROUTE_PATHS.dashboardHome)}>{t("team.goToDashboard")}</a>
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" className="min-w-[100px]" asChild>
                <a href={getDashboardDomainUrl(ROUTE_PATHS.login)}>{t("nav.signIn")}</a>
              </Button>
              <Button size="sm" className="min-w-[128px] bg-accent text-accent-foreground hover:bg-accent/90" asChild>
                <Link to="/pricing">{t("nav.startFree")}</Link>
              </Button>
            </>
          )}
        </div>

        <button
          type="button"
          className="justify-self-end md:hidden"
          onClick={() => setMobileOpen((prev) => !prev)}
          aria-label={t("topbar.mobileMenu")}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileMenuRendered && (
        <div
          className={`overflow-hidden border-t border-border bg-background px-4 md:hidden transition-all duration-200 ease-out ${
            mobileMenuVisible ? "max-h-[360px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div
            className={`flex flex-col gap-3 pt-3 pb-4 transition-all duration-200 ease-out ${
              mobileMenuVisible ? "translate-y-0" : "-translate-y-3"
            }`}
          >
            {navLinks.map((link) =>
              link.href.startsWith("/#") ? (
                <button
                  key={link.label}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left text-sm font-medium text-muted-foreground"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-sm font-medium text-muted-foreground"
                >
                  {link.label}
                </Link>
              ),
            )}
            <LanguageSwitcher compact className="mt-2 self-start" />
            <div className="flex gap-2 pt-2">
              {user ? (
                <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" asChild>
                  <a href={getDashboardDomainUrl(ROUTE_PATHS.dashboardHome)}>{t("team.goToDashboard")}</a>
                </Button>
              ) : (
                <>
                  <Button variant="ghost" size="sm" asChild>
                    <a href={getDashboardDomainUrl(ROUTE_PATHS.login)}>{t("nav.signIn")}</a>
                  </Button>
                  <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90" asChild>
                    <Link to="/pricing">{t("nav.startFree")}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
